import { useEffect, useState } from "react";
import {
  Container,
  Table,
  Button,
  Card,
  Row,
  Col,
  Badge,
  Alert,
  Spinner,
  Modal,
  Form,
} from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import {
  getTicketsByOrgId,
  microsoftOAuthApi,
  organizationEmailApi,
  emailProcessingApi,
  createTicket,
  updateTicket,
  attachmentApi,
} from "../Api/TicketApiOrg";
import { useAuth } from "../Context/AuthContext";
import { fetchProjectsByOrg } from "../Api/ViewProjects";

const ViewTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);

  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const userId = userDetails?.userId;
  const orgId = userDetails?.organisationId;

  const [connectionStatus, setConnectionStatus] = useState(null);
  const [organizationEmails, setOrganizationEmails] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showTicketInfoModal, setShowTicketInfoModal] = useState(false); // New state for ticket info modal
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newEmail, setNewEmail] = useState({ emailType: "", emailId: "" });
  const [testResults, setTestResults] = useState(null);
  const [updateForm, setUpdateForm] = useState({
    ticketName: "",
    description: "",
    status: "",
    priority: "",
    assignedTo: "",
    category: "",
    dueDate: "",
    referenceTo: "",
    projectAssigned: "",
    ticketType: "",
  });
  const { user } = useAuth();
  const [attachments, setAttachments] = useState({});
  const [loadingAttachments, setLoadingAttachments] = useState({});
  const [showAttachmentsModal, setShowAttachmentsModal] = useState(false);
  const [selectedTicketAttachments, setSelectedTicketAttachments] = useState(
    []
  );
  // Search and Sort States
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  useEffect(() => {
    if (user?.organisationId) {
      fetchTickets();
      checkConnectionStatus();
      fetchOrganizationEmails();
      fetchOrganizationProjects();
    }
  }, [user]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      if (!user?.organisationId) {
        console.warn("Organisation ID not found in user context.");
        return;
      }

      console.log("Fetching tickets for orgId:", user.organisationId);
      const data = await getTicketsByOrgId(user.organisationId);
      console.log("Fetched tickets:", data);
      setTickets(data);
    } catch (err) {
      console.error("Failed to load tickets", err);
      toast.error("Failed to load tickets. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizationProjects = async () => {
    try {
      if (!user?.organisationId) return;

      const projectsData = await fetchProjectsByOrg(user.organisationId);
      setProjects(projectsData);
    } catch (error) {
      console.error("Error fetching organization projects:", error);
      toast.error("Failed to load projects");
    }
  };

  // Helper function to get project name by ID
  const getProjectNameById = (projectId) => {
    if (!projectId) return "N/A";
    const project = projects.find(
      (proj) => proj.projectId === projectId || proj.prozmaPrId === projectId
    );
    return project ? project.prTitle : projectId;
  };

  // Helper function to get project details by ID
  const getProjectDetailsById = (projectId) => {
    if (!projectId) return null;
    return projects.find(
      (proj) => proj.projectId === projectId || proj.prozmaPrId === projectId
    );
  };

  const checkConnectionStatus = async () => {
    try {
      const status = await microsoftOAuthApi.getConnectionStatus(
        user.organisationId
      );
      setConnectionStatus(status);
    } catch (error) {
      console.error("Error checking connection status:", error);
      setConnectionStatus({ isConnected: false });
    }
  };

  const fetchOrganizationEmails = async () => {
    try {
      const emails = await organizationEmailApi.getEmailsByOrgId(
        user.organisationId
      );
      setOrganizationEmails(emails);
    } catch (error) {
      console.error("Error fetching organization emails:", error);
    }
  };

  const handleConnectMicrosoft = async () => {
    try {
      const result = await microsoftOAuthApi.connectOrganization(
        user.organisationId
      );
      // Redirect to Microsoft login
      window.location.href = result.authorizationUrl;
    } catch (error) {
      console.error("Error starting OAuth flow:", error);
      toast.error("Failed to start Microsoft connection process");
    }
  };

  const handleDisconnectMicrosoft = async () => {
    try {
      await microsoftOAuthApi.disconnectOrganization(user.organisationId);
      setConnectionStatus({ isConnected: false });
      toast.success("Successfully disconnected from Microsoft account");
      checkConnectionStatus();
    } catch (error) {
      console.error("Error disconnecting:", error);
      toast.error("Failed to disconnect from Microsoft account");
    }
  };

  const handleTestEmailConnection = async () => {
    try {
      setTestResults({ testing: true });
      const result = await emailProcessingApi.testEmailConnection(
        user.organisationId
      );
      setTestResults({
        success: true,
        message: "Email connection test successful!",
      });
      toast.success("Email connection test successful!");
    } catch (error) {
      setTestResults({
        success: false,
        message: "Email connection test failed",
      });
      toast.error("Email connection test failed");
    }
  };

  // Reset search and sort
  const handleResetFilters = () => {
    setSearchTerm("");
    setSortConfig({ key: null, direction: "asc" });
  };

  const handleProcessEmails = async () => {
    try {
      setLoading(true);
      await emailProcessingApi.processOrganizationEmails(user.organisationId);
      toast.success("Email processing started successfully!");
      // Refresh tickets after processing
      setTimeout(() => fetchTickets(), 2000);
    } catch (error) {
      console.error("Error processing emails:", error);
      toast.error("Failed to process emails");
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmail = async () => {
    try {
      if (!newEmail.emailType || !newEmail.emailId) {
        toast.error("Please fill in both email type and email address");
        return;
      }

      await organizationEmailApi.createOrganizationEmail({
        orgId: user.organisationId,
        emailType: newEmail.emailType,
        emailId: newEmail.emailId,
      });

      toast.success("Email added successfully!");
      setShowEmailModal(false);
      setNewEmail({ emailType: "", emailId: "" });
      fetchOrganizationEmails();
    } catch (error) {
      console.error("Error adding email:", error);
      toast.error("Failed to add email");
    }
  };

  const handleCreateTestTicket = async () => {
    try {
      const testTicket = {
        orgId: user.organisationId,
        ticketName: "Test Ticket from UI",
        description: "This is a test ticket created from the frontend UI",
        status: "Open",
        priority: "Medium",
        createdBy: user.username || "Test User",
        category: "test",
        fromMail: "test@example.com",
        projectAssigned: "PROJ001,PROJ002",
      };

      await createTicket(testTicket);
      toast.success("Test ticket created successfully!");
      fetchTickets(); // Refresh the list
    } catch (error) {
      console.error("Error creating test ticket:", error);
      toast.error("Failed to create test ticket");
    }
  };

  // Open ticket info modal
  const handleShowTicketInfo = (ticket) => {
    setSelectedTicket(ticket);
    setShowTicketInfoModal(true);
  };

  // Open update modal with ticket data
  const handleEditTicket = (ticket) => {
    setSelectedTicket(ticket);
    setUpdateForm({
      ticketName: ticket.ticketName || "",
      description: ticket.description || "",
      status: ticket.status || "Open",
      priority: ticket.priority || "Medium",
      assignedTo: ticket.assignedTo || "",
      category: ticket.category || "",
      referenceTo: ticket.referenceTo || "",
      dueDate: ticket.dueDate
        ? moment(ticket.dueDate).format("YYYY-MM-DD")
        : "",
      projectAssigned: ticket.projectAssigned || "",
      ticketType: ticket.ticketType || "Internal",
    });
    setShowUpdateModal(true);
  };

  // Handle individual project checkbox change
  const handleProjectCheckboxChange = (projectId, isChecked) => {
    const currentProjects = updateForm.projectAssigned
      ? updateForm.projectAssigned.split(",").filter((id) => id !== "")
      : [];

    let updatedProjects;
    if (isChecked) {
      // Add project if checked
      updatedProjects = [...currentProjects, projectId];
    } else {
      // Remove project if unchecked
      updatedProjects = currentProjects.filter((id) => id !== projectId);
    }

    setUpdateForm({
      ...updateForm,
      projectAssigned: updatedProjects.join(","),
    });
  };

  // Check if a project is selected
  const isProjectSelected = (projectId) => {
    if (!updateForm.projectAssigned) return false;
    const selectedProjects = updateForm.projectAssigned.split(",");
    return selectedProjects.includes(projectId);
  };

  // Update ticket
  const handleUpdateTicket = async () => {
    try {
      if (!selectedTicket) return;

      setLoading(true);
      await updateTicket(selectedTicket.ticketId, updateForm);

      toast.success("Ticket updated successfully!");
      setShowUpdateModal(false);
      setSelectedTicket(null);
      fetchTickets(); // Refresh the list
    } catch (error) {
      console.error("Error updating ticket:", error);
      toast.error("Failed to update ticket");
    } finally {
      setLoading(false);
    }
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      critical: "danger",
      high: "warning",
      medium: "primary",
      low: "secondary",
      normal: "info",
    };
    return (
      <Badge bg={variants[priority?.toLowerCase()] || "secondary"}>
        {priority}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    const variants = {
      open: "success",
      closed: "secondary",
      "in progress": "warning",
      resolved: "info",
    };
    return (
      <Badge bg={variants[status?.toLowerCase()] || "secondary"}>
        {status}
      </Badge>
    );
  };

  // Updated formatProjectAssigned to show project names
  const formatProjectAssigned = (projectAssigned) => {
    if (!projectAssigned) return "N/A";

    // If it's a comma-separated string, split and display as badges with project names
    if (projectAssigned.includes(",")) {
      return projectAssigned.split(",").map((projectId, index) => {
        const projectName = getProjectNameById(projectId.trim());
        return (
          <Badge
            key={index}
            bg="outline-primary"
            className="me-1 mb-1"
            style={{ fontSize: "12px", color: "black" }}
            title={`Project ID: ${projectId.trim()}`}
          >
            {projectName}
          </Badge>
        );
      });
    }

    // Single project
    const projectName = getProjectNameById(projectAssigned);
    return (
      <Badge
        bg="outline-primary"
        style={{ fontSize: "12px", color: "black" }}
        title={`Project ID: ${projectAssigned}`}
      >
        {projectName}
      </Badge>
    );
  };

  // Fetch attachments for a specific ticket
  // Fetch attachments for a specific ticket
  const fetchTicketAttachments = async (ticketId) => {
    try {
      setLoadingAttachments((prev) => ({ ...prev, [ticketId]: true }));
      const attachmentData = await attachmentApi.getTicketAttachments(ticketId);

      // Ensure we have a proper data structure
      const processedData = {
        AttachmentCount: attachmentData?.AttachmentCount || 0,
        AttachmentNames: attachmentData?.AttachmentNames || [],
        AzureBlobUrls: attachmentData?.AzureBlobUrls || [],
        TotalAttachmentSize: attachmentData?.TotalAttachmentSize || 0,
        StorageInfo: attachmentData?.StorageInfo || "No storage info available",
      };

      setAttachments((prev) => ({ ...prev, [ticketId]: processedData }));
      return processedData;
    } catch (error) {
      console.error(
        `Error fetching attachments for ticket ${ticketId}:`,
        error
      );
      toast.error("Failed to load attachments");

      // Return empty structure on error
      return {
        AttachmentCount: 0,
        AttachmentNames: [],
        AzureBlobUrls: [],
        TotalAttachmentSize: 0,
        StorageInfo: "Error loading attachments",
      };
    } finally {
      setLoadingAttachments((prev) => ({ ...prev, [ticketId]: false }));
    }
  };

  // Handle download attachment
  const handleDownloadAttachment = async (blobUrl, fileName) => {
    try {
      const response = await attachmentApi.downloadAttachment(
        blobUrl,
        fileName
      );

      // Create blob and download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Downloaded: ${fileName}`);
    } catch (error) {
      console.error("Error downloading attachment:", error);
      toast.error("Failed to download attachment");
    }
  };

  // Handle view attachment in browser
  const handleViewAttachment = async (blobUrl, fileName) => {
    try {
      const response = await attachmentApi.viewAttachment(blobUrl, fileName);

      // Create blob and open in new tab
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");

      // Clean up after some time
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error("Error viewing attachment:", error);
      toast.error("Failed to view attachment");
    }
  };

  // Show attachments modal
  const handleShowAttachments = async (ticket) => {
    setSelectedTicketAttachments([]);
    setShowAttachmentsModal(true);

    // Check if we have direct attachment data in the ticket
    if (ticket.attachmentCount > 0 && ticket.attachmentPaths) {
      const urls = ticket.attachmentPaths.split(";");
      const names = ticket.attachmentNames
        ? ticket.attachmentNames.split(";")
        : [];

      const attachmentsList = urls.map((url, index) => ({
        url: url.trim(),
        name: names[index] ? names[index].trim() : `Attachment ${index + 1}`,
        index,
      }));

      setSelectedTicketAttachments(attachmentsList);
    } else {
      // Fallback to API call if direct data is not available
      try {
        const attachmentData = await fetchTicketAttachments(ticket.ticketId);
        if (attachmentData && attachmentData.AzureBlobUrls) {
          const attachmentsList = attachmentData.AzureBlobUrls.map(
            (url, index) => ({
              url,
              name:
                attachmentData.AttachmentNames?.[index] ||
                `Attachment ${index + 1}`,
              index,
            })
          );
          setSelectedTicketAttachments(attachmentsList);
        } else {
          setSelectedTicketAttachments([]);
        }
      } catch (error) {
        console.error("Error processing attachments:", error);
        setSelectedTicketAttachments([]);
      }
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return "Unknown size";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / 1048576).toFixed(1) + " MB";
  };

  // Get file icon based on extension
  const getFileIcon = (fileName) => {
    const extension = fileName?.split(".").pop()?.toLowerCase();
    switch (extension) {
      case "pdf":
        return "📄";
      case "doc":
      case "docx":
        return "📝";
      case "xls":
      case "xlsx":
        return "📊";
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return "🖼️";
      case "zip":
      case "rar":
        return "📦";
      case "txt":
        return "📃";
      default:
        return "📎";
    }
  };

  // Filter tickets based on search
  const filteredTickets = tickets
    .filter((ticket) =>
      ticket.ticketName?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (!sortConfig.key) return 0;
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (sortConfig.key === "ticketTime") {
        const aDate = new Date(aValue);
        const bDate = new Date(bValue);
        return sortConfig.direction === "asc" ? aDate - bDate : bDate - aDate;
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

  // Sort toggle function
  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      } else {
        return { key, direction: "asc" };
      }
    });
  };

  return (
    <div className="main-content expand">
      <Container fluid className="list-container">
        {/* Connection Status Card — visible only to Executive, Manager, or Admin */}
        {user?.roles?.some((role) => ["Admin"].includes(role)) && (
          <Row className="mb-4">
            <Col md={12}>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Microsoft Teams Connection</h5>
                </Card.Header>
                <Card.Body
                  style={{
                    display: "flex",
                    gap: "10px",
                    flexDirection: "column",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>Status: </strong>
                      <Badge
                        bg={
                          connectionStatus?.isConnected ? "success" : "danger"
                        }
                        className="ms-2"
                      >
                        {connectionStatus?.isConnected
                          ? "Connected"
                          : "Not Connected"}
                      </Badge>
                    </div>
                    <div>
                      {connectionStatus?.isConnected ? (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={handleDisconnectMicrosoft}
                        >
                          Disconnect
                        </Button>
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleConnectMicrosoft}
                        >
                          Connect Microsoft Account
                        </Button>
                      )}
                    </div>
                  </div>
                  <span style={{ fontSize: "10px" }}>
                    Note : Make Sure to Connect to Microsoft Account Shown below
                    in Registered Emails to Activate Ticket Creation
                  </span>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Organization Emails Card */}
        {organizationEmails.length > 0 && (
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Registered Emails</h5>
                </Card.Header>
                <Card.Body>
                  <Table striped bordered size="sm">
                    <thead>
                      <tr>
                        <th>Email Type</th>
                        <th>Email Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      {organizationEmails.map((email, index) => (
                        <tr key={index}>
                          <td>{email.emailType}</td>
                          <td>{email.emailId}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Tickets Table */}
        {/* Tickets Table */}
        <Card>
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Tickets</h5>
              <div style={{ display: "flex" }} >
                {/* Search Input */}
                <Form.Control
                  type="text"
                  placeholder="Search by Ticket Name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: "250px", marginRight: "10px" }}
                />
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="me-2"
                  onClick={handleResetFilters}
                  style={{ padding : "10px" }}
                >
                  Reset
                </Button>
              </div>

              <div className="d-flex align-items-center">
                {/* Reset Button */}

                {/* Refresh Button */}
                <Badge bg="primary" className="me-2">
                  {filteredTickets.length} tickets
                </Badge>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={fetchTickets}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            {loading ? (
              <div className="text-center">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            ) : filteredTickets.length > 0 ? (
              <div className="table-wrapper">
                <div className="table-scroll-container">
                  <Table bordered hover className="data-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        {/* Sort by Ticket ID */}
                        <th
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort("ticketId")}
                        >
                          Ticket ID{" "}
                          {sortConfig.key === "ticketId" && (
                            <span>
                              {sortConfig.direction === "asc" ? "▲" : "▼"}
                            </span>
                          )}
                        </th>

                        {/* Sort by Ticket Name */}
                        <th>Ticket Name</th>

                        {/* Sort by Ticket Time */}
                        <th
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort("ticketTime")}
                        >
                          Ticket Time{" "}
                          {sortConfig.key === "ticketTime" && (
                            <span>
                              {sortConfig.direction === "asc" ? "▲" : "▼"}
                            </span>
                          )}
                        </th>

                        <th>Description</th>
                        <th>Status</th>
                        <th>Ticket Type</th>
                        <th>Priority</th>
                        <th>Reference To</th>
                        <th>Created By</th>
                        <th>Assigned To</th>
                        <th>From Mail</th>
                        <th>Category</th>
                        <th>Project Assigned</th>
                        <th>Attachments</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTickets.map((ticket, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>
                            <Badge bg="dark">{ticket.ticketId || "N/A"}</Badge>
                          </td>
                          <td>
                            <strong
                              style={{ cursor: "pointer", color: "#007bff" }}
                              onClick={() => handleShowTicketInfo(ticket)}
                              title="Click to view full ticket information"
                            >
                              {ticket.ticketName || "N/A"}
                            </strong>
                          </td>
                          <td>
                            {ticket.ticketTime
                              ? moment(ticket.ticketTime).format(
                                  "MMM DD, YYYY HH:mm"
                                )
                              : "N/A"}
                          </td>
                          <td>
                            <div style={{ maxWidth: "200px" }}>
                              {ticket.description
                                ? ticket.description.length > 100
                                  ? `${ticket.description.substring(0, 100)}...`
                                  : ticket.description
                                : "N/A"}
                            </div>
                          </td>
                          <td>{getStatusBadge(ticket.status)}</td>
                          <td>
                            <Badge bg="outline-info" style={{ color: "black" }}>
                              {ticket.ticketType || "No Type"}
                            </Badge>
                          </td>
                          <td>{getPriorityBadge(ticket.priority)}</td>
                          <td>{ticket.referenceTo || "N/A"}</td>
                          <td>{ticket.createdBy || "N/A"}</td>
                          <td>{ticket.assignedTo || "N/A"}</td>
                          <td>
                            <small>{ticket.fromMail || "N/A"}</small>
                          </td>
                          <td>
                            <Badge bg="info">{ticket.category || "N/A"}</Badge>
                          </td>
                          <td>
                            {formatProjectAssigned(ticket.projectAssigned)}
                          </td>
                          <td>
                            {ticket.attachmentCount > 0 ? (
                              <div className="d-flex align-items-center">
                                <Badge bg="success" className="me-2">
                                  {ticket.attachmentCount}
                                </Badge>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleShowAttachments(ticket)}
                                  disabled={loadingAttachments[ticket.ticketId]}
                                >
                                  {loadingAttachments[ticket.ticketId] ? (
                                    <Spinner animation="border" size="sm" />
                                  ) : (
                                    "View"
                                  )}
                                </Button>
                              </div>
                            ) : (
                              <span className="text-muted">No attachments</span>
                            )}
                          </td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEditTicket(ticket)}
                            >
                              Edit
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            ) : (
              <Alert variant="info" className="text-center">
                No tickets found.
              </Alert>
            )}
          </Card.Body>
        </Card>

        {/* Ticket Information Modal */}
        <Modal
          show={showTicketInfoModal}
          onHide={() => setShowTicketInfoModal(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Ticket Information: {selectedTicket?.ticketId}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedTicket && (
              <div>
                <Row className="mb-3">
                  <Col md={6}>
                    <strong>Ticket Name:</strong>
                    <p>{selectedTicket.ticketName || "N/A"}</p>
                  </Col>
                  <Col md={6}>
                    <strong>Ticket ID:</strong>
                    <p>
                      <Badge bg="dark">
                        {selectedTicket.ticketId || "N/A"}
                      </Badge>
                    </p>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <strong>Status:</strong>
                    <p>{getStatusBadge(selectedTicket.status)}</p>
                  </Col>
                  <Col md={6}>
                    <strong>Priority:</strong>
                    <p>{getPriorityBadge(selectedTicket.priority)}</p>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <strong>Ticket Type:</strong>
                    <p>
                      <Badge bg="outline-info" style={{ color: "black" }}>
                        {selectedTicket.ticketType || "No Type"}
                      </Badge>
                    </p>
                  </Col>
                  <Col md={6}>
                    <strong>Category:</strong>
                    <p>
                      <Badge bg="info">
                        {selectedTicket.category || "N/A"}
                      </Badge>
                    </p>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <strong>Created By:</strong>
                    <p>{selectedTicket.createdBy || "N/A"}</p>
                  </Col>
                  <Col md={6}>
                    <strong>Assigned To:</strong>
                    <p>{selectedTicket.assignedTo || "N/A"}</p>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <strong>From Email:</strong>
                    <p>{selectedTicket.fromMail || "N/A"}</p>
                  </Col>
                  <Col md={6}>
                    <strong>Ticket Time:</strong>
                    <p>
                      {selectedTicket.ticketTime
                        ? moment(selectedTicket.ticketTime).format(
                            "MMM DD, YYYY HH:mm"
                          )
                        : "N/A"}
                    </p>
                  </Col>
                </Row>

                {selectedTicket.dueDate && (
                  <Row className="mb-3">
                    <Col md={6}>
                      <strong>Due Date:</strong>
                      <p>
                        {moment(selectedTicket.dueDate).format("MMM DD, YYYY")}
                      </p>
                    </Col>
                  </Row>
                )}

                <Row className="mb-3">
                  <Col md={12}>
                    <strong>Project Assigned:</strong>
                    <div className="mt-1">
                      {formatProjectAssigned(selectedTicket.projectAssigned)}
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={12}>
                    <strong>Description:</strong>
                    <div
                      style={{
                        maxHeight: "200px",
                        overflowY: "auto",
                        border: "1px solid #e9ecef",
                        borderRadius: "0.375rem",
                        padding: "10px",
                        backgroundColor: "#f8f9fa",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {selectedTicket.description || "No description provided"}
                    </div>
                  </Col>
                </Row>

                {/* Add this to the Ticket Information Modal, after the Description section */}
                <Row className="mb-3">
                  <Col md={12}>
                    <strong>Attachments:</strong>
                    <div className="mt-2">
                      {selectedTicket?.attachmentCount > 0 ? (
                        <div>
                          <div className="d-flex align-items-center mb-2">
                            <Badge bg="success" className="me-2">
                              {selectedTicket.attachmentCount} file(s)
                            </Badge>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => {
                                setShowTicketInfoModal(false);
                                handleShowAttachments(selectedTicket);
                              }}
                            >
                              View All Attachments
                            </Button>
                          </div>
                          {selectedTicket.attachmentNames && (
                            <div className="mt-1">
                              <small className="text-muted">
                                Files:{" "}
                                {selectedTicket.attachmentNames
                                  .split(";")
                                  .join(", ")}
                              </small>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted">No attachments</span>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowTicketInfoModal(false)}
            >
              Close
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setShowTicketInfoModal(false);
                handleEditTicket(selectedTicket);
              }}
            >
              Edit Ticket
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Add Email Modal */}
        <Modal show={showEmailModal} onHide={() => setShowEmailModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Add Organization Email</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Email Type</Form.Label>
                <Form.Select
                  value={newEmail.emailType}
                  onChange={(e) =>
                    setNewEmail({ ...newEmail, emailType: e.target.value })
                  }
                >
                  <option value="">Select email type...</option>
                  <option value="support">Support</option>
                  <option value="sales">Sales</option>
                  <option value="general">General</option>
                  <option value="billing">Billing</option>
                  <option value="technical">Technical</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter email address"
                  value={newEmail.emailId}
                  onChange={(e) =>
                    setNewEmail({ ...newEmail, emailId: e.target.value })
                  }
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowEmailModal(false)}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleAddEmail}>
              Add Email
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Update Ticket Modal */}
        <Modal
          show={showUpdateModal}
          onHide={() => setShowUpdateModal(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Update Ticket: {selectedTicket?.ticketId}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedTicket && (
              <Form>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Ticket Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={updateForm.ticketName}
                        onChange={(e) =>
                          setUpdateForm({
                            ...updateForm,
                            ticketName: e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Status</Form.Label>
                      <Form.Select
                        value={updateForm.status}
                        onChange={(e) =>
                          setUpdateForm({
                            ...updateForm,
                            status: e.target.value,
                          })
                        }
                      >
                        <option value="Open">Open</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Resolved">Resolved</option>
                        <option value="Closed">Closed</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Ticket Type</Form.Label>
                      <Form.Select
                        value={updateForm.ticketType}
                        onChange={(e) =>
                          setUpdateForm({
                            ...updateForm,
                            ticketType: e.target.value,
                          })
                        }
                      >
                        <option value="Internal">Internal</option>
                        <option value="Client">Client</option>
                        <option value="Waiting for client">
                          Waiting for client
                        </option>
                        <option value="Technical">Technical</option>
                        <option value="Support">Support</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={updateForm.description}
                    onChange={(e) =>
                      setUpdateForm({
                        ...updateForm,
                        description: e.target.value,
                      })
                    }
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Reference To</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={updateForm.referenceTo}
                    onChange={(e) =>
                      setUpdateForm({
                        ...updateForm,
                        referenceTo: e.target.value,
                      })
                    }
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Priority</Form.Label>
                      <Form.Select
                        value={updateForm.priority}
                        onChange={(e) =>
                          setUpdateForm({
                            ...updateForm,
                            priority: e.target.value,
                          })
                        }
                      >
                        <option value="Low">Low</option>
                        <option value="Normal">Normal</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                        <option value="Critical">Critical</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Category</Form.Label>
                      <Form.Control
                        type="text"
                        value={updateForm.category}
                        onChange={(e) =>
                          setUpdateForm({
                            ...updateForm,
                            category: e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Assigned To</Form.Label>
                      <Form.Control
                        type="text"
                        value={updateForm.assignedTo}
                        onChange={(e) =>
                          setUpdateForm({
                            ...updateForm,
                            assignedTo: e.target.value,
                          })
                        }
                        placeholder="Assign to user..."
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Due Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={updateForm.dueDate}
                        onChange={(e) =>
                          setUpdateForm({
                            ...updateForm,
                            dueDate: e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Project Assigned</Form.Label>
                  <div
                    style={{
                      maxHeight: "200px",
                      overflowY: "auto",
                      border: "1px solid #ced4da",
                      borderRadius: "0.375rem",
                      padding: "10px",
                      backgroundColor: "#f8f9fa",
                    }}
                  >
                    {projects.length > 0 ? (
                      projects.map((project) => (
                        <Form.Check
                          key={project.projectId}
                          type="checkbox"
                          id={`project-${project.projectId}`}
                          label={`${project.prTitle} (${project.projectId})`}
                          checked={isProjectSelected(project.projectId)}
                          onChange={(e) =>
                            handleProjectCheckboxChange(
                              project.projectId,
                              e.target.checked
                            )
                          }
                          className="mb-2"
                        />
                      ))
                    ) : (
                      <div className="text-muted">No projects available</div>
                    )}
                  </div>
                  <Form.Text className="text-muted">
                    Select one or multiple projects to assign this ticket to
                  </Form.Text>
                  {updateForm.projectAssigned && (
                    <div className="mt-2">
                      <strong>Selected Projects:</strong>
                      <div className="mt-1">
                        {updateForm.projectAssigned.split(",").map(
                          (projectId, index) =>
                            projectId && (
                              <Badge
                                key={index}
                                bg="primary"
                                className="me-1 mb-1"
                                title={`Project ID: ${projectId}`}
                              >
                                {getProjectNameById(projectId)}
                              </Badge>
                            )
                        )}
                      </div>
                    </div>
                  )}
                </Form.Group>
              </Form>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowUpdateModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleUpdateTicket}
              disabled={loading}
            >
              {loading ? "Updating..." : "Update Ticket"}
            </Button>
          </Modal.Footer>
        </Modal>
        {/* Attachments Modal */}
        <Modal
          show={showAttachmentsModal}
          onHide={() => setShowAttachmentsModal(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Attachments for Ticket: {selectedTicket?.ticketId || "Unknown"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedTicketAttachments &&
            selectedTicketAttachments.length > 0 ? (
              <div>
                <p className="text-muted mb-3">
                  {selectedTicketAttachments.length} attachment(s) found
                  {attachments[selectedTicket?.ticketId]?.TotalAttachmentSize >
                    0 &&
                    ` • Total size: ${formatFileSize(
                      attachments[selectedTicket?.ticketId]?.TotalAttachmentSize
                    )}`}
                </p>

                <div className="attachments-list">
                  {selectedTicketAttachments.map((attachment, index) => (
                    <Card key={index} className="mb-2">
                      <Card.Body className="py-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center">
                            <span
                              className="me-2"
                              style={{ fontSize: "1.2em" }}
                            >
                              {getFileIcon(attachment.name)}
                            </span>
                            <div>
                              <div className="fw-bold">{attachment.name}</div>
                              <small className="text-muted">
                                {attachment.url ? "Available" : "Not available"}
                              </small>
                            </div>
                          </div>
                          <div>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="me-1"
                              onClick={() =>
                                handleViewAttachment(
                                  attachment.url,
                                  attachment.name
                                )
                              }
                              title="View in browser"
                              disabled={!attachment.url}
                            >
                              👁️ View
                            </Button>
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() =>
                                handleDownloadAttachment(
                                  attachment.url,
                                  attachment.name
                                )
                              }
                              title="Download file"
                              disabled={!attachment.url}
                            >
                              ⬇️ Download
                            </Button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <div className="text-muted">
                  No attachments found for this ticket
                </div>
                <small className="text-muted">
                  This ticket either has no attachments or the attachment data
                  is not available.
                </small>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowAttachmentsModal(false)}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default ViewTickets;

// import { useEffect, useState } from "react";
// import {
//   Container,
//   Table,
//   Button,
//   Card,
//   Row,
//   Col,
//   Badge,
//   Alert,
//   Spinner,
//   Modal,
//   Form,
// } from "react-bootstrap";
// import { toast } from "react-toastify";
// import moment from "moment";
// import {
//   getTicketsByOrgId,
//   microsoftOAuthApi,
//   organizationEmailApi,
//   emailProcessingApi,
//   createTicket,
//   updateTicket,
// } from "../Api/TicketApiOrg";
// import { useAuth } from "../Context/AuthContext";
// import { fetchProjectsByOrg } from "../Api/ViewProjects";

// const ViewTickets = () => {
//   const [tickets, setTickets] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [projects, setProjects] = useState([]);

//   const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//   const userId = userDetails?.userId;
//   const orgId = userDetails?.organisationId;

//   const [connectionStatus, setConnectionStatus] = useState(null);
//   const [organizationEmails, setOrganizationEmails] = useState([]);
//   const [showEmailModal, setShowEmailModal] = useState(false);
//   const [showUpdateModal, setShowUpdateModal] = useState(false);
//   const [selectedTicket, setSelectedTicket] = useState(null);
//   const [newEmail, setNewEmail] = useState({ emailType: "", emailId: "" });
//   const [testResults, setTestResults] = useState(null);
//   const [updateForm, setUpdateForm] = useState({
//     ticketName: "",
//     description: "",
//     status: "",
//     priority: "",
//     assignedTo: "",
//     category: "",
//     dueDate: "",
//     projectAssigned: "",
//     ticketType: "",
//   });
//   const { user } = useAuth();

//   useEffect(() => {
//     if (user?.organisationId) {
//       fetchTickets();
//       checkConnectionStatus();
//       fetchOrganizationEmails();
//       fetchOrganizationProjects();
//     }
//   }, [user]);

//   const fetchTickets = async () => {
//     try {
//       setLoading(true);
//       if (!user?.organisationId) {
//         console.warn("Organisation ID not found in user context.");
//         return;
//       }

//       console.log("Fetching tickets for orgId:", user.organisationId);
//       const data = await getTicketsByOrgId(user.organisationId);
//       setTickets(data);
//     } catch (err) {
//       console.error("Failed to load tickets", err);
//       toast.error("Failed to load tickets. Please try again later.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const fetchOrganizationProjects = async () => {
//     try {
//       if (!user?.organisationId) return;

//       const projectsData = await fetchProjectsByOrg(user.organisationId);
//       setProjects(projectsData);
//     } catch (error) {
//       console.error("Error fetching organization projects:", error);
//       toast.error("Failed to load projects");
//     }
//   };

//   // Helper function to get project name by ID
//   const getProjectNameById = (projectId) => {
//     if (!projectId) return "N/A";
//     const project = projects.find(
//       (proj) => proj.projectId === projectId || proj.prozmaPrId === projectId
//     );
//     return project ? project.prTitle : projectId;
//   };

//   // Helper function to get project details by ID
//   const getProjectDetailsById = (projectId) => {
//     if (!projectId) return null;
//     return projects.find(
//       (proj) => proj.projectId === projectId || proj.prozmaPrId === projectId
//     );
//   };

//   const checkConnectionStatus = async () => {
//     try {
//       const status = await microsoftOAuthApi.getConnectionStatus(
//         user.organisationId
//       );
//       setConnectionStatus(status);
//     } catch (error) {
//       console.error("Error checking connection status:", error);
//       setConnectionStatus({ isConnected: false });
//     }
//   };

//   const fetchOrganizationEmails = async () => {
//     try {
//       const emails = await organizationEmailApi.getEmailsByOrgId(
//         user.organisationId
//       );
//       setOrganizationEmails(emails);
//     } catch (error) {
//       console.error("Error fetching organization emails:", error);
//     }
//   };

//   const handleConnectMicrosoft = async () => {
//     try {
//       const result = await microsoftOAuthApi.connectOrganization(
//         user.organisationId
//       );
//       // Redirect to Microsoft login
//       window.location.href = result.authorizationUrl;
//     } catch (error) {
//       console.error("Error starting OAuth flow:", error);
//       toast.error("Failed to start Microsoft connection process");
//     }
//   };

//   const handleDisconnectMicrosoft = async () => {
//     try {
//       await microsoftOAuthApi.disconnectOrganization(user.organisationId);
//       setConnectionStatus({ isConnected: false });
//       toast.success("Successfully disconnected from Microsoft account");
//       checkConnectionStatus();
//     } catch (error) {
//       console.error("Error disconnecting:", error);
//       toast.error("Failed to disconnect from Microsoft account");
//     }
//   };

//   const handleTestEmailConnection = async () => {
//     try {
//       setTestResults({ testing: true });
//       const result = await emailProcessingApi.testEmailConnection(
//         user.organisationId
//       );
//       setTestResults({
//         success: true,
//         message: "Email connection test successful!",
//       });
//       toast.success("Email connection test successful!");
//     } catch (error) {
//       setTestResults({
//         success: false,
//         message: "Email connection test failed",
//       });
//       toast.error("Email connection test failed");
//     }
//   };

//   const handleProcessEmails = async () => {
//     try {
//       setLoading(true);
//       await emailProcessingApi.processOrganizationEmails(user.organisationId);
//       toast.success("Email processing started successfully!");
//       // Refresh tickets after processing
//       setTimeout(() => fetchTickets(), 2000);
//     } catch (error) {
//       console.error("Error processing emails:", error);
//       toast.error("Failed to process emails");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleAddEmail = async () => {
//     try {
//       if (!newEmail.emailType || !newEmail.emailId) {
//         toast.error("Please fill in both email type and email address");
//         return;
//       }

//       await organizationEmailApi.createOrganizationEmail({
//         orgId: user.organisationId,
//         emailType: newEmail.emailType,
//         emailId: newEmail.emailId,
//       });

//       toast.success("Email added successfully!");
//       setShowEmailModal(false);
//       setNewEmail({ emailType: "", emailId: "" });
//       fetchOrganizationEmails();
//     } catch (error) {
//       console.error("Error adding email:", error);
//       toast.error("Failed to add email");
//     }
//   };

//   const handleCreateTestTicket = async () => {
//     try {
//       const testTicket = {
//         orgId: user.organisationId,
//         ticketName: "Test Ticket from UI",
//         description: "This is a test ticket created from the frontend UI",
//         status: "Open",
//         priority: "Medium",
//         createdBy: user.username || "Test User",
//         category: "test",
//         fromMail: "test@example.com",
//         projectAssigned: "PROJ001,PROJ002",
//       };

//       await createTicket(testTicket);
//       toast.success("Test ticket created successfully!");
//       fetchTickets(); // Refresh the list
//     } catch (error) {
//       console.error("Error creating test ticket:", error);
//       toast.error("Failed to create test ticket");
//     }
//   };

//   // Open update modal with ticket data
//   const handleEditTicket = (ticket) => {
//     setSelectedTicket(ticket);
//     setUpdateForm({
//       ticketName: ticket.ticketName || "",
//       description: ticket.description || "",
//       status: ticket.status || "Open",
//       priority: ticket.priority || "Medium",
//       assignedTo: ticket.assignedTo || "",
//       category: ticket.category || "",
//       dueDate: ticket.dueDate
//         ? moment(ticket.dueDate).format("YYYY-MM-DD")
//         : "",
//       projectAssigned: ticket.projectAssigned || "",
//       ticketType: ticket.ticketType || "Internal", // Add this line with default value
//     });
//     setShowUpdateModal(true);
//   };

//   // Handle individual project checkbox change
//   const handleProjectCheckboxChange = (projectId, isChecked) => {
//     const currentProjects = updateForm.projectAssigned
//       ? updateForm.projectAssigned.split(",").filter((id) => id !== "")
//       : [];

//     let updatedProjects;
//     if (isChecked) {
//       // Add project if checked
//       updatedProjects = [...currentProjects, projectId];
//     } else {
//       // Remove project if unchecked
//       updatedProjects = currentProjects.filter((id) => id !== projectId);
//     }

//     setUpdateForm({
//       ...updateForm,
//       projectAssigned: updatedProjects.join(","),
//     });
//   };

//   // Check if a project is selected
//   const isProjectSelected = (projectId) => {
//     if (!updateForm.projectAssigned) return false;
//     const selectedProjects = updateForm.projectAssigned.split(",");
//     return selectedProjects.includes(projectId);
//   };

//   // Update ticket
//   const handleUpdateTicket = async () => {
//     try {
//       if (!selectedTicket) return;

//       setLoading(true);
//       await updateTicket(selectedTicket.ticketId, updateForm);

//       toast.success("Ticket updated successfully!");
//       setShowUpdateModal(false);
//       setSelectedTicket(null);
//       fetchTickets(); // Refresh the list
//     } catch (error) {
//       console.error("Error updating ticket:", error);
//       toast.error("Failed to update ticket");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const getPriorityBadge = (priority) => {
//     const variants = {
//       critical: "danger",
//       high: "warning",
//       medium: "primary",
//       low: "secondary",
//       normal: "info",
//     };
//     return (
//       <Badge bg={variants[priority?.toLowerCase()] || "secondary"}>
//         {priority}
//       </Badge>
//     );
//   };

//   const getStatusBadge = (status) => {
//     const variants = {
//       open: "success",
//       closed: "secondary",
//       "in progress": "warning",
//       resolved: "info",
//     };
//     return (
//       <Badge bg={variants[status?.toLowerCase()] || "secondary"}>
//         {status}
//       </Badge>
//     );
//   };

//   // Updated formatProjectAssigned to show project names
//   const formatProjectAssigned = (projectAssigned) => {
//     if (!projectAssigned) return "N/A";

//     // If it's a comma-separated string, split and display as badges with project names
//     if (projectAssigned.includes(",")) {
//       return projectAssigned.split(",").map((projectId, index) => {
//         const projectName = getProjectNameById(projectId.trim());
//         return (
//           <Badge
//             key={index}
//             bg="outline-primary"
//             className="me-1 mb-1"
//             style={{ fontSize: "0.7em", color: "black" }}
//             title={`Project ID: ${projectId.trim()}`}
//           >
//             {projectName}
//           </Badge>
//         );
//       });
//     }

//     // Single project
//     const projectName = getProjectNameById(projectAssigned);
//     return (
//       <Badge
//         bg="outline-primary"
//         style={{ fontSize: "0.7em", color: "black" }}
//         title={`Project ID: ${projectAssigned}`}
//       >
//         {projectName}
//       </Badge>
//     );
//   };

//   return (
//     <div className="main-content expand">
//       <Container fluid className="list-container">
//         {/* <div className="d-flex justify-content-between align-items-center mb-4">
//           <span className="list-title">Ticket Management</span>
//         </div> */}

//         {/* Connection Status Card — visible only to Executive, Manager, or Admin */}
//         {user?.roles?.some((role) => ["Admin"].includes(role)) && (
//           <Row className="mb-4">
//             <Col md={12}>
//               <Card>
//                 <Card.Header>
//                   <h5 className="mb-0">Microsoft Teams Connection</h5>
//                 </Card.Header>
//                 <Card.Body
//                   style={{
//                     display: "flex",
//                     gap: "10px",
//                     flexDirection: "column",
//                   }}
//                 >
//                   <div className="d-flex justify-content-between align-items-center">
//                     <div>
//                       <strong>Status: </strong>
//                       <Badge
//                         bg={
//                           connectionStatus?.isConnected ? "success" : "danger"
//                         }
//                         className="ms-2"
//                       >
//                         {connectionStatus?.isConnected
//                           ? "Connected"
//                           : "Not Connected"}
//                       </Badge>
//                     </div>
//                     <div>
//                       {connectionStatus?.isConnected ? (
//                         <Button
//                           variant="outline-danger"
//                           size="sm"
//                           onClick={handleDisconnectMicrosoft}
//                         >
//                           Disconnect
//                         </Button>
//                       ) : (
//                         <Button
//                           variant="primary"
//                           size="sm"
//                           onClick={handleConnectMicrosoft}
//                         >
//                           Connect Microsoft Account
//                         </Button>
//                       )}
//                     </div>
//                   </div>
//                   <span style={{ fontSize: "10px" }}>
//                     Note : Make Sure to Connect to Microsoft Account Shown below
//                     in Registered Emails to Activate Ticket Creation
//                   </span>
//                 </Card.Body>
//               </Card>
//             </Col>
//           </Row>
//         )}

//         {/* Organization Emails Card */}
//         {organizationEmails.length > 0 && (
//           <Row className="mb-4">
//             <Col>
//               <Card>
//                 <Card.Header>
//                   <h5 className="mb-0">Registered Emails</h5>
//                 </Card.Header>
//                 <Card.Body>
//                   <Table striped bordered size="sm">
//                     <thead>
//                       <tr>
//                         <th>Email Type</th>
//                         <th>Email Address</th>
//                       </tr>
//                     </thead>
//                     <tbody>
//                       {organizationEmails.map((email, index) => (
//                         <tr key={index}>
//                           <td>{email.emailType}</td>
//                           <td>{email.emailId}</td>
//                         </tr>
//                       ))}
//                     </tbody>
//                   </Table>
//                 </Card.Body>
//               </Card>
//             </Col>
//           </Row>
//         )}

//         {/* Tickets Table */}
//         <Card>
//           <Card.Header>
//             <div className="d-flex justify-content-between align-items-center">
//               <h5 className="mb-0">Tickets</h5>
//               <div
//                 style={{
//                   display: "flex",
//                   flexDirection: "row",
//                   alignItems: "center",
//                 }}
//               >
//                 <Badge bg="primary" className="me-2">
//                   {tickets.length} tickets
//                 </Badge>
//                 <Button
//                   variant="outline-secondary"
//                   size="sm"
//                   onClick={fetchTickets}
//                   disabled={loading}
//                 >
//                   Refresh
//                 </Button>
//               </div>
//             </div>
//           </Card.Header>
//           <Card.Body>
//             {loading ? (
//               <div className="text-center">
//                 <Spinner animation="border" role="status">
//                   <span className="visually-hidden">Loading...</span>
//                 </Spinner>
//               </div>
//             ) : tickets.length > 0 ? (
//               <div className="table-wrapper">
//                 <div className="table-scroll-container">
//                   <Table bordered hover className="data-table">
//                     <thead>
//                       <tr>
//                         <th>#</th>
//                         <th>Ticket ID</th>
//                         <th>Ticket Name</th>
//                         <th>Ticket Time</th>
//                         <th>Description</th>
//                         <th>Status</th>
//                         <th>Ticket Type</th>
//                         <th>Priority</th>
//                         <th>Created By</th>
//                         <th>Assigned To</th>
//                         <th>From Mail</th>
//                         <th>Category</th>
//                         <th>Project Assigned</th>
//                         <th>Actions</th>
//                       </tr>
//                     </thead>
//                     <tbody>
//                       {tickets.map((ticket, index) => (
//                         <tr key={index}>
//                           <td>{index + 1}</td>
//                           <td>
//                             <Badge bg="dark">{ticket.ticketId || "N/A"}</Badge>
//                           </td>
//                           <td>
//                             <strong>{ticket.ticketName || "N/A"}</strong>
//                           </td>
//                           <td>
//                             {ticket.ticketTime
//                               ? moment(ticket.ticketTime).format(
//                                   "MMM DD, YYYY HH:mm"
//                                 )
//                               : "N/A"}
//                           </td>
//                           <td>
//                             <div
//                               style={{
//                                 maxWidth: "200px",
//                                 textWrap: "auto",
//                                 width: "15vw",
//                               }}
//                             >
//                               {ticket.description
//                                 ? ticket.description.length > 100
//                                   ? `${ticket.description.substring(0, 100)}...`
//                                   : ticket.description
//                                 : "N/A"}
//                             </div>
//                           </td>
//                           <td>{getStatusBadge(ticket.status)}</td>
//                           <td>
//                             <Badge bg="outline-info" style={{ color: "black" }}>
//                               {ticket.ticketType || "No Type"}
//                             </Badge>
//                           </td>
//                           <td>{getPriorityBadge(ticket.priority)}</td>
//                           <td>{ticket.createdBy || "N/A"}</td>
//                           <td>{ticket.assignedTo || "N/A"}</td>
//                           <td>
//                             <small>{ticket.fromMail || "N/A"}</small>
//                           </td>
//                           <td>
//                             <Badge bg="info">{ticket.category || "N/A"}</Badge>
//                           </td>
//                           <td style={{ verticalAlign: "middle" }}>
//                             <div
//                               style={{
//                                 maxWidth: "300px",
//                                 whiteSpace: "normal",
//                                 wordWrap: "break-word",
//                                 display: "flex",
//                                 flexWrap: "wrap",
//                                 gap: "4px",
//                               }}
//                             >
//                               {formatProjectAssigned(ticket.projectAssigned)}
//                             </div>
//                           </td>

//                           <td>
//                             <Button
//                               variant="outline-primary"
//                               size="sm"
//                               onClick={() => handleEditTicket(ticket)}
//                             >
//                               Edit
//                             </Button>
//                           </td>
//                         </tr>
//                       ))}
//                     </tbody>
//                   </Table>
//                 </div>
//               </div>
//             ) : (
//               <Alert variant="info" className="text-center">
//                 No tickets found.
//                 {connectionStatus?.isConnected
//                   ? " Try processing emails or create a test ticket."
//                   : " Connect your Microsoft account to start generating tickets from emails."}
//               </Alert>
//             )}
//           </Card.Body>
//         </Card>

//         {/* Add Email Modal */}
//         <Modal show={showEmailModal} onHide={() => setShowEmailModal(false)}>
//           <Modal.Header closeButton>
//             <Modal.Title>Add Organization Email</Modal.Title>
//           </Modal.Header>
//           <Modal.Body>
//             <Form>
//               <Form.Group className="mb-3">
//                 <Form.Label>Email Type</Form.Label>
//                 <Form.Select
//                   value={newEmail.emailType}
//                   onChange={(e) =>
//                     setNewEmail({ ...newEmail, emailType: e.target.value })
//                   }
//                 >
//                   <option value="">Select email type...</option>
//                   <option value="support">Support</option>
//                   <option value="sales">Sales</option>
//                   <option value="general">General</option>
//                   <option value="billing">Billing</option>
//                   <option value="technical">Technical</option>
//                 </Form.Select>
//               </Form.Group>
//               <Form.Group className="mb-3">
//                 <Form.Label>Email Address</Form.Label>
//                 <Form.Control
//                   type="email"
//                   placeholder="Enter email address"
//                   value={newEmail.emailId}
//                   onChange={(e) =>
//                     setNewEmail({ ...newEmail, emailId: e.target.value })
//                   }
//                 />
//               </Form.Group>
//             </Form>
//           </Modal.Body>
//           <Modal.Footer>
//             <Button
//               variant="secondary"
//               onClick={() => setShowEmailModal(false)}
//             >
//               Cancel
//             </Button>
//             <Button variant="primary" onClick={handleAddEmail}>
//               Add Email
//             </Button>
//           </Modal.Footer>
//         </Modal>

//         {/* Update Ticket Modal */}
//         <Modal
//           show={showUpdateModal}
//           onHide={() => setShowUpdateModal(false)}
//           size="lg"
//         >
//           <Modal.Header closeButton>
//             <Modal.Title>Update Ticket: {selectedTicket?.ticketId}</Modal.Title>
//           </Modal.Header>
//           <Modal.Body>
//             {selectedTicket && (
//               <Form>
//                 <Row>
//                   <Col md={6}>
//                     <Form.Group className="mb-3">
//                       <Form.Label>Ticket Name</Form.Label>
//                       <Form.Control
//                         type="text"
//                         value={updateForm.ticketName}
//                         onChange={(e) =>
//                           setUpdateForm({
//                             ...updateForm,
//                             ticketName: e.target.value,
//                           })
//                         }
//                       />
//                     </Form.Group>
//                   </Col>
//                   <Col md={6}>
//                     <Form.Group className="mb-3">
//                       <Form.Label>Status</Form.Label>
//                       <Form.Select
//                         value={updateForm.status}
//                         onChange={(e) =>
//                           setUpdateForm({
//                             ...updateForm,
//                             status: e.target.value,
//                           })
//                         }
//                       >
//                         <option value="Open">Open</option>
//                         <option value="In Progress">In Progress</option>
//                         <option value="Resolved">Resolved</option>
//                         <option value="Closed">Closed</option>
//                       </Form.Select>
//                     </Form.Group>
//                   </Col>
//                 </Row>
//                 <Row>
//                   {/* Add this after the Status field in the modal */}
//                   <Col md={6}>
//                     <Form.Group className="mb-3">
//                       <Form.Label>Ticket Type</Form.Label>
//                       <Form.Select
//                         value={updateForm.ticketType}
//                         onChange={(e) =>
//                           setUpdateForm({
//                             ...updateForm,
//                             ticketType: e.target.value,
//                           })
//                         }
//                       >
//                         <option value="Internal">Internal</option>
//                         <option value="Client">Client</option>
//                         <option value="Waiting for client">
//                           Waiting for client
//                         </option>
//                         <option value="Technical">Technical</option>
//                         <option value="Support">Support</option>
//                       </Form.Select>
//                     </Form.Group>
//                   </Col>
//                 </Row>

//                 <Form.Group className="mb-3">
//                   <Form.Label>Description</Form.Label>
//                   <Form.Control
//                     as="textarea"
//                     rows={3}
//                     value={updateForm.description}
//                     onChange={(e) =>
//                       setUpdateForm({
//                         ...updateForm,
//                         description: e.target.value,
//                       })
//                     }
//                   />
//                 </Form.Group>

//                 <Row>
//                   <Col md={6}>
//                     <Form.Group className="mb-3">
//                       <Form.Label>Priority</Form.Label>
//                       <Form.Select
//                         value={updateForm.priority}
//                         onChange={(e) =>
//                           setUpdateForm({
//                             ...updateForm,
//                             priority: e.target.value,
//                           })
//                         }
//                       >
//                         <option value="Low">Low</option>
//                         <option value="Normal">Normal</option>
//                         <option value="Medium">Medium</option>
//                         <option value="High">High</option>
//                         <option value="Critical">Critical</option>
//                       </Form.Select>
//                     </Form.Group>
//                   </Col>
//                   <Col md={6}>
//                     <Form.Group className="mb-3">
//                       <Form.Label>Category</Form.Label>
//                       <Form.Control
//                         type="text"
//                         value={updateForm.category}
//                         onChange={(e) =>
//                           setUpdateForm({
//                             ...updateForm,
//                             category: e.target.value,
//                           })
//                         }
//                       />
//                     </Form.Group>
//                   </Col>
//                 </Row>

//                 <Row>
//                   <Col md={6}>
//                     <Form.Group className="mb-3">
//                       <Form.Label>Assigned To</Form.Label>
//                       <Form.Control
//                         type="text"
//                         value={updateForm.assignedTo}
//                         onChange={(e) =>
//                           setUpdateForm({
//                             ...updateForm,
//                             assignedTo: e.target.value,
//                           })
//                         }
//                         placeholder="Assign to user..."
//                       />
//                     </Form.Group>
//                   </Col>
//                   <Col md={6}>
//                     <Form.Group className="mb-3">
//                       <Form.Label>Due Date</Form.Label>
//                       <Form.Control
//                         type="date"
//                         value={updateForm.dueDate}
//                         onChange={(e) =>
//                           setUpdateForm({
//                             ...updateForm,
//                             dueDate: e.target.value,
//                           })
//                         }
//                       />
//                     </Form.Group>
//                   </Col>
//                 </Row>

//                 <Form.Group className="mb-3">
//                   <Form.Label>Project Assigned</Form.Label>
//                   <div
//                     style={{
//                       maxHeight: "200px",
//                       overflowY: "auto",
//                       border: "1px solid #ced4da",
//                       borderRadius: "0.375rem",
//                       padding: "10px",
//                       backgroundColor: "#f8f9fa",
//                     }}
//                   >
//                     {projects.length > 0 ? (
//                       projects.map((project) => (
//                         <Form.Check
//                           key={project.projectId}
//                           type="checkbox"
//                           id={`project-${project.projectId}`}
//                           label={`${project.prTitle} (${project.projectId})`}
//                           checked={isProjectSelected(project.projectId)}
//                           onChange={(e) =>
//                             handleProjectCheckboxChange(
//                               project.projectId,
//                               e.target.checked
//                             )
//                           }
//                           className="mb-2"
//                         />
//                       ))
//                     ) : (
//                       <div className="text-muted">No projects available</div>
//                     )}
//                   </div>
//                   <Form.Text className="text-muted">
//                     Select one or multiple projects to assign this ticket to
//                   </Form.Text>
//                   {updateForm.projectAssigned && (
//                     <div className="mt-2">
//                       <strong>Selected Projects:</strong>
//                       <div className="mt-1">
//                         {updateForm.projectAssigned.split(",").map(
//                           (projectId, index) =>
//                             projectId && (
//                               <Badge
//                                 key={index}
//                                 bg="primary"
//                                 className="me-1 mb-1"
//                                 title={`Project ID: ${projectId}`}
//                               >
//                                 {getProjectNameById(projectId)}
//                               </Badge>
//                             )
//                         )}
//                       </div>
//                     </div>
//                   )}
//                 </Form.Group>
//               </Form>
//             )}
//           </Modal.Body>
//           <Modal.Footer>
//             <Button
//               variant="secondary"
//               onClick={() => setShowUpdateModal(false)}
//             >
//               Cancel
//             </Button>
//             <Button
//               variant="primary"
//               onClick={handleUpdateTicket}
//               disabled={loading}
//             >
//               {loading ? "Updating..." : "Update Ticket"}
//             </Button>
//           </Modal.Footer>
//         </Modal>
//       </Container>
//     </div>
//   );
// };

// export default ViewTickets;
